import type { PlatformBridge, FileFilter } from '@slate/core';
import { useDocStore } from './docStore';
import { isImageFile } from '../util/fileType';
import { defaultFilenameForDoc } from '../util/filenameFromHeading';
import {
  extensionsForChoice,
  languageChoice,
  langKeyForDoc,
  primaryExtForChoice,
} from '../util/docLanguage';

function basename(p: string): string {
  return p.split(/[\\/]/).pop() ?? p;
}

/** Markdown's filter extensions (the historical default for the open dialog and
 *  the second entry in every save filter list). `txt` is included so plain-text
 *  notes show up alongside markdown. */
const MARKDOWN_EXTENSIONS = ['md', 'markdown', 'mdown', 'mkd', 'txt'];

/** The unconstrained "anything goes" save filter. The adapters treat `['*']`
 *  (or empty) as "no extension constraint" so a name with any/no extension can
 *  be saved (see Tasks 8/9). */
const ALL_FILES_FILTER: FileFilter = { name: 'All Files', extensions: ['*'] };

/** Lowercase extension (no dot) of `name`, or '' when there's none. */
function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Build the Save dialog filters for a language choice key, deduped:
 *   1. the language's own extensions (skipped when the language IS markdown,
 *      since the markdown filter below already covers it),
 *   2. a Markdown filter (`md, markdown, mdown, mkd, txt`),
 *   3. All Files (so any extension, or none, can be chosen).
 * The active language always leads so its primary extension is the default.
 */
export function filtersForLanguage(choiceKey: string): FileFilter[] {
  const filters: FileFilter[] = [];
  const choice = languageChoice(choiceKey);
  const isMarkdown = choiceKey === 'markdown';
  if (choice && !isMarkdown) {
    const exts = extensionsForChoice(choiceKey);
    if (exts.length > 0) filters.push({ name: choice.label, extensions: exts });
  }
  filters.push({ name: 'Markdown', extensions: [...MARKDOWN_EXTENSIONS] });
  filters.push(ALL_FILES_FILTER);
  return filters;
}

/**
 * Shared document actions used by BOTH the Toolbar buttons and the App-level
 * keyboard shortcuts, so Save/Open behave identically however they're invoked.
 * These are imperative helpers (they read the live store via `getState()`),
 * which is exactly what a global keydown handler needs.
 */

/**
 * Open a file by PATH (or synthetic web id), routing on type: an image opens in
 * the read-only image viewer (no text read — the viewer reads bytes from `ref`
 * on demand); everything else is read as text and opened in the editor. The
 * single entry point the file tree / search / drop / OS-open paths funnel
 * through. Throws are left to the caller (each opener already guards).
 */
export async function openPath(platform: PlatformBridge, path: string): Promise<void> {
  const store = useDocStore.getState();
  if (isImageFile(basename(path))) {
    const ref = { id: path, path, name: basename(path) };
    store.openImageDoc(ref);
    void platform.pushRecentFile(ref).catch(() => {});
    return;
  }
  const { ref, content } = await platform.readFile(path);
  store.openDoc(ref, content);
  void platform.pushRecentFile(ref).catch(() => {});
}

/** Open the file dialog and, if a file is chosen, open it as a new document.
 *  Markdown leads the filter list, then All Files so any text file can be opened. */
export async function openFileToDoc(platform: PlatformBridge): Promise<void> {
  const opened = await platform.openFileDialog({ filters: filtersForLanguage('markdown') });
  if (opened) {
    useDocStore.getState().openDoc(opened.ref, opened.content);
    void platform.pushRecentFile(opened.ref).catch(() => {});
  }
}

/** Save the active doc to a NEW path via the save dialog (Save As). The dialog's
 *  filters and the suggested filename come from the doc's CURRENT language (a
 *  `/lang` override or its real extension): a markdown doc suggests its first
 *  heading (`# Notes` -> `Notes.md`), a TypeScript doc suggests `untitled.ts`,
 *  etc. File-backed docs keep their existing basename as the suggestion.
 *
 *  Precedence (decision): after a successful Save As to a path with a RECOGNIZED
 *  extension, the `/lang` override is cleared so the new extension re-derives the
 *  language. A save to a path with NO extension leaves the current language
 *  intact. */
export async function saveActiveDocAs(platform: PlatformBridge): Promise<void> {
  const store = useDocStore.getState();
  const cur = store.activeDoc();
  const langKey = cur ? langKeyForDoc(cur) : 'plaintext';
  const primaryExt = primaryExtForChoice(langKey);
  const defaultPath = cur?.ref?.name ?? defaultFilenameForDoc(cur?.text ?? '', primaryExt);
  const path = await platform.saveFileDialog({
    defaultPath,
    filters: filtersForLanguage(langKey),
  });
  if (!path) return;
  await platform.writeFile(path, store.activeDoc()?.text ?? '');
  const name = path.split(/[\\/]/).pop() ?? `untitled.${primaryExt}`;
  // The saved id becomes the path (markSaved adopts the ref). Capture it so we
  // can clear the override on the now-renamed doc below.
  store.markSaved({ id: path, path, name });
  // Extension precedence: a recognized extension re-derives the language, so
  // drop any `/lang` override. A no-extension save keeps the current language.
  if (extOf(name) !== '') {
    useDocStore.getState().setDocLanguage(path, null);
  }
}

/**
 * Save the active document. Writes back to its existing ref (desktop: path;
 * web: stored handle) when present, falling back to Save As if there's no ref
 * or the write fails (e.g. permission denied / handle lost).
 */
export async function saveActiveDoc(platform: PlatformBridge): Promise<void> {
  const cur = useDocStore.getState().activeDoc();
  // Image docs are read-only — never write (an empty-text write would corrupt
  // the file). Guarded here so EVERY Save entry point (shortcut, toolbar) is safe.
  if (cur?.kind === 'image') return;
  if (cur?.ref) {
    try {
      await platform.writeFile(cur.ref, cur.text);
      useDocStore.getState().markSaved(cur.ref);
      return;
    } catch {
      /* fall through to Save As */
    }
  }
  await saveActiveDocAs(platform);
}

/**
 * Save a specific doc by id (used by the close guard — the doc being closed is
 * not necessarily the active one). Activates it first so the active-doc save +
 * markSaved machinery (incl. Save As for untitled buffers) applies to it.
 * Returns whether the doc ended up saved/clean (false → user cancelled Save As).
 * Never throws.
 */
export async function saveDocById(platform: PlatformBridge, id: string): Promise<boolean> {
  const store = useDocStore.getState();
  const doc = store.docs.find((d) => d.id === id);
  if (!doc) return true; // already gone — treat as resolved
  if (!doc.dirty) return true; // nothing to save
  // Untitled buffers adopt a new id on save (markSaved), so track by ref/text:
  // after the save, the doc is clean iff a doc with the original OR adopted id
  // is no longer dirty. We detect success by re-reading the (possibly remapped)
  // doc — simplest robust check is "no dirty doc with this id remains".
  if (store.activeId !== id) store.activateDoc(id);
  try {
    await saveActiveDoc(platform);
  } catch {
    return false;
  }
  // After saveActiveDoc, an untitled buffer's id became its new ref.id; a cancel
  // of Save As leaves the original (still-dirty) doc untouched. So: clean iff no
  // dirty doc with the original id remains.
  const after = useDocStore.getState().docs.find((d) => d.id === id);
  return !after || !after.dirty;
}

/**
 * Save every dirty doc (used by the window-close "Save all" path). Saves each
 * by id; returns true only if ALL dirty docs ended up clean (a cancelled Save As
 * on any untitled buffer yields false so the caller can abort the window close).
 * Never throws.
 */
export async function saveAllDocs(platform: PlatformBridge): Promise<boolean> {
  // Snapshot ids up front; markSaved may remap an untitled id mid-loop.
  const dirtyIds = useDocStore
    .getState()
    .docs.filter((d) => d.dirty)
    .map((d) => d.id);
  let allSaved = true;
  for (const id of dirtyIds) {
    const ok = await saveDocById(platform, id);
    if (!ok) allSaved = false;
  }
  return allSaved;
}
